<template>
  <b-container fluid class="bg-white">
    <div class="row">
      <div class="col-lg-12">
        <b-table
          fixed
          small
          :striped="!onClientView"
          :hover="onClientView"
          borderless
          responsive
          caption-top
          sort-icon-left
          no-local-sorting
          table-class="lizard-candesic-table"
          thead-class="label"
          tbody-class="value"
          :sticky-header="stickyHeader"
          :busy.sync="loading"
          :items="listItems"
          :fields="filteredFields"
          :per-page="pager.perPage"
          :current-page="pager.page"
          @sort-changed="handleSortChanged">

          <!-- Table Caption -->
          <template #table-caption>
            <span v-if="loading" class="table-caption-loading">
              <strong>
                <b-spinner small></b-spinner>
                &nbsp;Loading...
              </strong>
            </span>
            <span v-else class="table-caption-info">
              <strong>
                Total results based on selected filters: {{ listItems.length }}
                <span v-if="listItems.length > pager.perPage">
                  | Page {{ pager.page }}/{{ totalPages }}
                </span>
              </strong>
            </span>
          </template>

          <!-- Row Details (Notes Section) -->
          <template #row-details="row">
            <b-card>
              <b-row class="mb-2">
                <b-col>
                  <b-form-textarea
                    v-model="row.item.note"
                    :placeholder="getNotePlaceholder(row.item.asset)"
                    rows="2"
                    no-resize
                    trim></b-form-textarea>
                </b-col>
              </b-row>

              <b-row class="mb-2 text-right">
                <b-col>
                  <b-button
                    size="sm"
                    variant="secondary"
                    @click="row.toggleDetails">
                    Close
                  </b-button>
                  &nbsp;
                  <b-button
                    size="sm"
                    variant="info"
                    :disabled="isSubmittingNote"
                    @click="handleWriteNote(row.item, row.toggleDetails)">
                    {{ isSubmittingNote ? 'Submitting...' : 'Submit' }}
                  </b-button>
                </b-col>
              </b-row>

              <b-row class="mb-2 text-left">
                <b-col>
                  <ul class="notes-list">
                    <li v-for="note in row.item.notes" :key="note.uid">
                      <span class="date">
                        [{{ formatNoteDate(note.createdDate) }}]
                      </span>
                      <span
                        class="author"
                        v-b-tooltip.hover.bottom="{ variant: 'info' }"
                        :title="getNoteAuthorFullName(note)">
                        {{ getNoteAuthorInitials(note) }}:
                      </span>
                      <span class="note">
                        {{ note.content }}
                      </span>
                    </li>
                  </ul>
                </b-col>
              </b-row>
            </b-card>
          </template>

          <!-- Note Icon Cell -->
          <template #cell(note)="row">
            <el-button
              icon="el-icon-document-add"
              class="note-button"
              size="mini"
              v-b-tooltip.hover.bottom="{ variant: 'dark' }"
              title="Add Note"
              @click="handleGetNotes(row.item, row.toggleDetails)"></el-button>
          </template>

          <!-- Asset Cell with OneNote Link -->
          <template #cell(asset)="data">
            <div @click="toggleSelectedAsset(data.item)">
              <b-link
                v-if="hasLink(data.item, 'onenote')"
                v-b-tooltip.hover.bottom="{ variant: 'dark' }"
                :href="getItemLink(data.item, 'onenote')"
                target="_blank"
                :title="getLinkTitle(data.item.asset, 'OneNote page')">
                {{ data.item.asset }}
              </b-link>
              <span v-else>{{ data.item.asset }}</span>
            </div>
          </template>

          <!-- Pitchbook Link Cell -->
          <template #cell(pitchbook)="data">
            <b-link
              v-if="hasLink(data.item, 'pitchbook')"
              :href="getItemLink(data.item, 'pitchbook')"
              target="_blank"
              v-b-tooltip.hover.bottom="{ variant: 'dark' }"
              :title="getLinkTitle(data.item.asset, 'Pitchbook')">
              Pitchpack
            </b-link>
          </template>

          <!-- Merger Market Link Cell -->
          <template #cell(mmlink)="data">
            <b-link
              v-if="hasLink(data.item, 'mergermarket')"
              :href="getItemLink(data.item, 'mergermarket')"
              target="_blank"
              v-b-tooltip.hover.bottom="{ variant: 'dark' }"
              :title="getLinkTitle(data.item.asset, 'Merger market profile')">
              MM
            </b-link>
          </template>

          <!-- Website Link Cell -->
          <template #cell(weblink)="data">
            <b-link
              v-if="hasLink(data.item, 'website')"
              :href="getItemLink(data.item, 'website')"
              target="_blank"
              v-b-tooltip.hover.bottom="{ variant: 'dark' }"
              :title="getLinkTitle(data.item.asset, 'Webpage')">
              WEB
            </b-link>
          </template>

          <!-- Partners Cell with Avatars -->
          <template #cell(partners)="data">
            <div
              v-for="(partner, index) in data.item.partners"
              :key="'partner-' + index"
              class="lizard-user-avatars-list"
              v-b-tooltip.hover.bottom="{ variant: 'dark' }"
              :title="partner">
              {{ getUserInitials(partner) }}
            </div>
          </template>

          <!-- EMs Cell with Avatars -->
          <template #cell(ems)="data">
            <div
              v-for="(em, index) in data.item.ems"
              :key="'em-' + index"
              class="lizard-user-avatars-list"
              v-b-tooltip.hover.bottom="{ variant: 'dark' }"
              :title="em">
              {{ getUserInitials(em) }}
            </div>
          </template>

          <!-- Standard Tooltipped Cells -->
          <template #cell(launch)="data">
            <div @click="toggleRejectedAsset(data.item)">
              <span
                v-b-tooltip.hover.bottom="{ variant: 'dark' }"
                :title="data.item.launch">
                {{ data.item.launch }}
              </span>
            </div>
          </template>

          <template #cell(description)="data">
            <span
              v-b-tooltip.hover.top="{ variant: 'dark' }"
              :title="data.item.description">
              {{ data.item.description }}
            </span>
          </template>

          <template #cell(comments)="data">
            <span
              v-b-tooltip.hover.top="{ variant: 'dark' }"
              :title="data.item.comments">
              {{ data.item.comments }}
            </span>
          </template>

          <template #cell(ebitda)="data">
            <span
              v-b-tooltip.hover.bottom="{ variant: 'dark' }"
              :title="data.item.ebitda">
              {{ data.item.ebitda }}
            </span>
          </template>

          <template #cell(subsector)="data">
            <span
              v-b-tooltip.hover.bottom="{ variant: 'dark' }"
              :title="data.item.subsector">
              {{ data.item.subsector }}
            </span>
          </template>

          <template #cell(process)="data">
            <span
              v-b-tooltip.hover.bottom="{ variant: 'dark' }"
              :title="data.item.process">
              {{ data.item.process }}
            </span>
          </template>

          <template #cell(priority)="data">
            <span
              v-b-tooltip.hover.bottom="{ variant: 'dark' }"
              :title="data.item.priority">
              {{ data.item.priority }}
            </span>
          </template>

          <template #cell(deck)="data">
            <span
              v-b-tooltip.hover.bottom="{ variant: 'dark' }"
              :title="data.item.deck">
              {{ data.item.deck }}
            </span>
          </template>

          <template #cell(owner)="data">
            <span
              v-b-tooltip.hover.bottom="{ variant: 'dark' }"
              :title="data.item.owner">
              {{ data.item.owner }}
            </span>
          </template>

          <template #cell(lastupdated)="data">
            <span
              v-b-tooltip.hover.bottom="{ variant: 'dark' }"
              :title="data.item.lastupdated">
              {{ data.item.lastupdated }}
            </span>
          </template>
        </b-table>
      </div>
    </div>

    <div class="row">
      <div class="col-lg lizard-form-empty-col"></div>
    </div>

    <!-- Pagination -->
    <div class="row" v-if="showPagination">
      <div class="col-lg">
        <b-pagination
          v-model="pager.page"
          :total-rows="listItems.length"
          :per-page="pager.perPage"
          @change="handlePageChange"
          aria-controls="activityTable"
          size="sm"
          pills
          align="center"></b-pagination>
      </div>
    </div>
  </b-container>
</template>

<script>
// ===== IMPORTS & DEPENDENCIES =====
import Services from '../../../services/main.service.vue'

// ===== CONFIGURATION & CONSTANTS =====
const FIELD_CLASSES = {
  COMMON: 'lizard-assetboard-tbl-cols',
  COMMON_HEAD: 'lizard-assetboard-tbl-cols-b-head',
  COMMON_CLIENT: 'lizard-assetboard-tbl-cols-b',
  BOLD: 'lizard-assetboard-tbl-cols-bold',
  TAGBOX: 'lizard-assetboard-tagbox'
}

// ===== COMPONENT DEFINITION =====
export default {
  name: 'TableView',

  props: {
    listItems: {
      type: Array,
      required: true,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    },
    onClientView: {
      type: Boolean,
      default: false
    },
    hideHqCountry: {
      type: Boolean,
      default: false
    }
  },

  created() {
    // Bind dynamic tdClass to asset column in Client View for highlighting
    const assetField = this.clientFields.find(f => f.key === 'asset')
    if (assetField) {
      assetField.tdClass = this.assetColumnClass
    }
    const launchField = this.clientFields.find(f => f.key === 'launch')
    if (assetField) {
      launchField.tdClass = this.launchColumnClass
    }
  },

  data() {
    return {
      stickyHeader: false,
      isSubmittingNote: false,

      // Admin view fields configuration
      adminFields: [
        {
          key: 'note',
          label: '',
          thClass: `${FIELD_CLASSES.COMMON} lizard-assetboard-tbl-cols-note text-left`,
          tdClass: `${FIELD_CLASSES.COMMON} lizard-assetboard-tbl-cols-note text-left`,
          stickyColumn: false,
          sortable: false
        },
        {
          key: 'asset',
          label: 'Asset',
          thClass: `${FIELD_CLASSES.COMMON} lizard-assetboard-tbl-cols-asset text-center`,
          tdClass: `${FIELD_CLASSES.COMMON} lizard-assetboard-tbl-cols-asset text-left`,
          stickyColumn: false,
          sortable: true
        },
        {
          key: 'partners',
          label: 'Partners',
          thClass: `${FIELD_CLASSES.COMMON} lizard-assetboard-tbl-cols-persons`,
          tdClass: `${FIELD_CLASSES.COMMON} lizard-assetboard-tbl-cols-persons`
        },
        {
          key: 'ems',
          label: 'P/EM',
          thClass: `${FIELD_CLASSES.COMMON} lizard-assetboard-tbl-cols-persons`,
          tdClass: `${FIELD_CLASSES.COMMON} lizard-assetboard-tbl-cols-persons`
        },
        {
          key: 'subsector',
          label: 'Subsector',
          thClass: `${FIELD_CLASSES.COMMON} lizard-assetboard-tbl-cols-labels`,
          tdClass: `${FIELD_CLASSES.COMMON} ${FIELD_CLASSES.TAGBOX} lizard-assetboard-tbl-cols-labels`,
          sortable: true
        },
        {
          key: 'process',
          label: 'Process',
          thClass: `${FIELD_CLASSES.COMMON} lizard-assetboard-tbl-cols-labels`,
          tdClass: `${FIELD_CLASSES.COMMON} ${FIELD_CLASSES.TAGBOX} lizard-assetboard-tbl-cols-labels`,
          sortable: true
        },
        {
          key: 'priority',
          label: 'Priority',
          thClass: `${FIELD_CLASSES.COMMON} lizard-assetboard-tbl-cols-labels`,
          tdClass: `${FIELD_CLASSES.COMMON} ${FIELD_CLASSES.TAGBOX} lizard-assetboard-tbl-cols-labels`,
          sortable: true
        },
        {
          key: 'deck',
          label: 'Deck',
          thClass: `${FIELD_CLASSES.COMMON} lizard-assetboard-tbl-cols-labels`,
          tdClass: `${FIELD_CLASSES.COMMON} ${FIELD_CLASSES.TAGBOX} lizard-assetboard-tbl-cols-labels`,
          sortable: true
        },
        {
          key: 'launch',
          label: 'Launch',
          thClass: `${FIELD_CLASSES.COMMON} lizard-assetboard-tbl-cols-other`,
          tdClass: `${FIELD_CLASSES.COMMON} lizard-assetboard-tbl-cols-other`
        },
        {
          key: 'ibank',
          label: 'IBank',
          thClass: `${FIELD_CLASSES.COMMON} lizard-assetboard-tbl-cols-other`,
          tdClass: `${FIELD_CLASSES.COMMON} lizard-assetboard-tbl-cols-other`
        },
        {
          key: 'description',
          label: 'Description',
          thClass: `${FIELD_CLASSES.COMMON} lizard-assetboard-tbl-cols-comments`,
          tdClass: `${FIELD_CLASSES.COMMON} lizard-assetboard-tbl-cols-comments text-left`
        },
        {
          key: 'comments',
          label: 'Comments',
          thClass: `${FIELD_CLASSES.COMMON} lizard-assetboard-tbl-cols-comments`,
          tdClass: `${FIELD_CLASSES.COMMON} lizard-assetboard-tbl-cols-comments text-left`
        },
        {
          key: 'ebitda',
          label: 'Est EBITDA',
          thClass: `${FIELD_CLASSES.COMMON} lizard-assetboard-tbl-cols-other`,
          tdClass: `${FIELD_CLASSES.COMMON} lizard-assetboard-tbl-cols-other`
        },
        {
          key: 'owner',
          label: 'Owner',
          thClass: `${FIELD_CLASSES.COMMON} lizard-assetboard-tbl-cols-owner`,
          tdClass: `${FIELD_CLASSES.COMMON} lizard-assetboard-tbl-cols-owner`,
          sortable: true
        },
        {
          key: 'hqcountry',
          label: 'HQ Country',
          thClass: `${FIELD_CLASSES.COMMON} lizard-assetboard-tbl-cols-other`,
          tdClass: `${FIELD_CLASSES.COMMON} lizard-assetboard-tbl-cols-other`,
          sortable: true
        },
        {
          key: 'lastupdated',
          label: 'Last Updated',
          thClass: `${FIELD_CLASSES.COMMON} lizard-assetboard-tbl-cols-other text-center`,
          tdClass: `${FIELD_CLASSES.COMMON} lizard-assetboard-tbl-cols-other text-center`,
          stickyColumn: false,
          sortable: false
        },
        {
          key: 'pitchbook',
          label: 'Pitchpack',
          thClass: `${FIELD_CLASSES.COMMON} lizard-assetboard-tbl-cols-mm text-center`,
          tdClass: `${FIELD_CLASSES.COMMON} lizard-assetboard-tbl-cols-mm text-left`,
          stickyColumn: false,
          sortable: false
        },
        {
          key: 'mmlink',
          label: 'MM',
          thClass: `${FIELD_CLASSES.COMMON} lizard-assetboard-tbl-cols-mm text-center`,
          tdClass: `${FIELD_CLASSES.COMMON} lizard-assetboard-tbl-cols-mm text-center`,
          stickyColumn: false,
          sortable: false
        },
        {
          key: 'weblink',
          label: 'Web',
          thClass: `${FIELD_CLASSES.COMMON} lizard-assetboard-tbl-cols-mm text-center`,
          tdClass: `${FIELD_CLASSES.COMMON} lizard-assetboard-tbl-cols-mm text-center`,
          stickyColumn: false,
          sortable: false
        }
      ],

      // Client view fields configuration
      clientFields: [
        {
          key: 'asset',
          label: 'Asset',
          thClass: `${FIELD_CLASSES.COMMON_HEAD} lizard-assetboard-tbl-cols-b-asset text-center`,
          tdClass: `${FIELD_CLASSES.COMMON_CLIENT} lizard-assetboard-tbl-cols-b-asset text-left ${FIELD_CLASSES.BOLD}`,
          stickyColumn: false,
          sortable: true
        },
        {
          key: 'subsector',
          label: 'Subsector',
          thClass: `${FIELD_CLASSES.COMMON_HEAD} lizard-assetboard-tbl-cols-b-labels`,
          tdClass: `${FIELD_CLASSES.COMMON_CLIENT} ${FIELD_CLASSES.TAGBOX} lizard-assetboard-tbl-cols-b-labels ${FIELD_CLASSES.BOLD}`,
          sortable: true
        },
        {
          key: 'launch',
          label: 'Launch',
          thClass: `${FIELD_CLASSES.COMMON_HEAD} lizard-assetboard-tbl-cols-b-launch`,
          tdClass: `${FIELD_CLASSES.COMMON_CLIENT} lizard-assetboard-tbl-cols-b-launch`
        },
        {
          key: 'ibank',
          label: 'IBank',
          thClass: `${FIELD_CLASSES.COMMON_HEAD} lizard-assetboard-tbl-cols-b-other`,
          tdClass: `${FIELD_CLASSES.COMMON_CLIENT} lizard-assetboard-tbl-cols-b-other`
        },
        {
          key: 'description',
          label: 'Description',
          thClass: `${FIELD_CLASSES.COMMON_HEAD} lizard-assetboard-tbl-cols-b-comments`,
          tdClass: `${FIELD_CLASSES.COMMON_CLIENT} lizard-assetboard-tbl-cols-b-comments text-left`
        },
        {
          key: 'comments',
          label: 'Comments',
          thClass: `${FIELD_CLASSES.COMMON_HEAD} lizard-assetboard-tbl-cols-b-comments`,
          tdClass: `${FIELD_CLASSES.COMMON_CLIENT} lizard-assetboard-tbl-cols-b-comments text-left`
        },
        {
          key: 'ebitda',
          label: 'Est EBITDA',
          thClass: `${FIELD_CLASSES.COMMON_HEAD} lizard-assetboard-tbl-cols-b-other`,
          tdClass: `${FIELD_CLASSES.COMMON_CLIENT} lizard-assetboard-tbl-cols-b-other`
        },
        {
          key: 'owner',
          label: 'Owner',
          thClass: `${FIELD_CLASSES.COMMON_HEAD} lizard-assetboard-tbl-cols-b-owner`,
          tdClass: `${FIELD_CLASSES.COMMON_CLIENT} lizard-assetboard-tbl-cols-b-owner`,
          sortable: true
        },
        {
          key: 'hqcountry',
          label: 'HQ Country',
          thClass: `${FIELD_CLASSES.COMMON_HEAD} lizard-assetboard-tbl-cols-b-other`,
          tdClass: `${FIELD_CLASSES.COMMON_CLIENT} lizard-assetboard-tbl-cols-b-other`,
          sortable: true
        }
      ]
    }
  },

  // ===== COMPUTED PROPERTIES =====
  computed: {
    /**
     * Get Vuex store getters
     */
    store() {
      return this.$store.getters
    },

    /**
     * Get pager configuration from store
     */
    pager() {
      return this.store.assetBoardPager
    },

    /**
     * Get filtered fields based on view mode
     */
    filteredFields() {
      const fields = this.onClientView ? this.clientFields : this.adminFields

      if (!this.hideHqCountry) return fields

      const withoutHqCountry = fields.filter(field => field.key !== 'hqcountry')
      const baseAssetClass = this.onClientView
        ? 'lizard-assetboard-tbl-cols-b-asset'
        : 'lizard-assetboard-tbl-cols-asset'
      const wideAssetClass = `${baseAssetClass}-wide`

      const widenClass = classStr => classStr.replace(baseAssetClass, wideAssetClass)

      return withoutHqCountry.map(field => {
        if (field.key !== 'asset') return field
        // In client view, tdClass for 'asset' is a per-row function (assetColumnClass)
        // rather than a static string - it must be wrapped, not string-replaced.
        return {
          ...field,
          thClass: widenClass(field.thClass),
          tdClass: typeof field.tdClass === 'function'
            ? (value, key, item) => widenClass(field.tdClass(value, key, item))
            : widenClass(field.tdClass)
        }
      })
    },

    /**
     * Calculate total number of pages
     */
    totalPages() {
      return Math.ceil(this.listItems.length / this.pager.perPage)
    },

    /**
     * Determine if pagination should be shown
     */
    showPagination() {
      return this.listItems.length > this.pager.perPage
    }
  },

  // ===== METHODS =====
  methods: {
    // ===== STORE METHODS =====

    /**
     * Commit mutation to Vuex store
     * @param {String} commitName - Mutation name
     * @param {*} value - Mutation payload
     */
    commitToStore(commitName, value) {
      this.$store.commit(commitName, value)
    },

    // ===== USER INTERFACE METHODS =====

    /**
     * Get user initials from full name (fixed bug: now returns consistent type)
     * @param {String} name - Full name
     * @returns {String} User initials
     */
    getUserInitials(name) {
      if (!name || typeof name !== 'string') {
        return ''
      }

      const trimmedName = name.trim()
      if (!trimmedName) {
        return ''
      }

      const names = trimmedName.split(/\s+/).filter(n => n.length > 0)

      if (names.length === 0) {
        return ''
      }

      if (names.length === 1) {
        return names[0].charAt(0).toUpperCase()
      }

      // Safely get initials from first and last name
      const firstInitial = names[0].charAt(0).toUpperCase()
      const lastInitial = names[names.length - 1].charAt(0).toUpperCase()

      return firstInitial + lastInitial
    },

    /**
     * Get placeholder text for note textarea
     * @param {String} assetName - Asset name
     * @returns {String} Placeholder text
     */
    getNotePlaceholder(assetName) {
      return `Write a note for the ${assetName || 'asset'}...`
    },

    /**
     * Get link title for tooltips
     * @param {String} assetName - Asset name
     * @param {String} linkType - Type of link
     * @returns {String} Tooltip title
     */
    getLinkTitle(assetName, linkType) {
      return `Click to visit the ${assetName}'s ${linkType}`
    },

    // ===== LINK METHODS =====

    /**
     * Check if item has a specific link
     * @param {Object} item - Table item
     * @param {String} linkKey - Link key (e.g., 'onenote', 'pitchbook')
     * @returns {Boolean} Whether link exists
     */
    hasLink(item, linkKey) {
      return Boolean(item?.meta?.links?.[linkKey])
    },

    /**
     * Get item link by key
     * @param {Object} item - Table item
     * @param {String} linkKey - Link key
     * @returns {String|null} Link URL or null
     */
    getItemLink(item, linkKey) {
      return item?.meta?.links?.[linkKey] || null
    },

    // ===== NOTE METHODS =====

    /**
     * Format note creation date
     * @param {String|Date} date - Date to format
     * @returns {String} Formatted date string
     */
    formatNoteDate(date) {
      try {
        return new Date(date).toLocaleString()
      } catch (error) {
        console.error('Error formatting date:', error)
        return 'Invalid date'
      }
    },

    /**
     * Get note author full name
     * @param {Object} note - Note object
     * @returns {String} Author full name
     */
    getNoteAuthorFullName(note) {
      const firstName = note?.author?.firstName || ''
      const lastName = note?.author?.lastName || ''
      return `${firstName} ${lastName}`.trim() || 'Unknown'
    },

    /**
     * Get note author initials
     * @param {Object} note - Note object
     * @returns {String} Author initials
     */
    getNoteAuthorInitials(note) {
      return this.getUserInitials(this.getNoteAuthorFullName(note))
    },

    /**
     * Handle note submission
     * @param {Object} asset - Asset object
     * @param {Function} callback - Callback to close details
     */
    async handleWriteNote(asset, callback = () => {}) {
      const noteContent = String(asset?.note ?? '').trim()

      if (noteContent === '') {
        this.showToast('Note cannot be empty', 'danger')
        return
      }

      this.isSubmittingNote = true

      try {
        const { status } = await Services.Assets.Admin.writeNote({
          assetUid: asset.uid,
          note: noteContent
        })

        if (status === 200) {
          this.showToast('Note added successfully', 'success')
          asset.note = ''
          callback()
        } else {
          this.showToast('Failed to add note', 'danger')
        }
      } catch (error) {
        console.error('Error writing note:', error)
        this.showToast('Failed to add note', 'danger')
      } finally {
        this.isSubmittingNote = false
      }
    },

    /**
     * Handle fetching notes for an asset
     * @param {Object} asset - Asset object
     * @param {Function} callback - Callback to toggle details
     */
    async handleGetNotes(asset, callback = () => {}) {
      try {
        const { status, data } = await Services.Assets.Admin.getNotes({
          assetUid: asset.uid
        })

        if (status === 200) {
          asset.notes = data?.content?.list || []
        }

        callback()
      } catch (error) {
        console.error('Error fetching notes:', error)
        this.showToast('Failed to load notes', 'danger')
      }
    },

    // ===== PAGINATION & SORTING METHODS =====

    /**
     * Handle page change event
     * @param {Number} page - New page number
     */
    handlePageChange(page) {
      this.commitToStore('setAssetBoardPager', { page })
    },

    /**
     * Handle sort change event
     * @param {Object} sortConfig - Sort configuration
     * @param {String} sortConfig.sortBy - Field to sort by
     * @param {Boolean} sortConfig.sortDesc - Sort direction
     */
    handleSortChanged({ sortBy, sortDesc }) {
      this.commitToStore('setAssetBoardSort', { sortBy, sortDesc })
      this.$root.$emit('fetch_table_view_data')
    },

    isSelectedAsset(item) {
      return this.$store.getters.assetBoardSelectedAssets.includes(item.uid)
    },
    
    isRejectedAsset(item) {
      return this.$store.getters.assetBoardRejectedAssets.includes(item.uid)
    },


    toggleSelectedAsset(item) {
      if (this.onClientView) {
        // console.log('to add:', item.uid)
        this.commitToStore('toggleSelectedAsset', item.uid)
      }
    },
    
    toggleRejectedAsset(item) {
      if (this.onClientView) {
        // console.log('to remove:', item.uid)
        this.commitToStore('toggleRejectedAsset', item.uid)
      }
    },

    /**
     * Dynamic tdClass for the asset column in Client View
     * Highlights the cell blue when the row is reserved
     */
    assetColumnClass(value, key, item) {
      const base = `${FIELD_CLASSES.COMMON_CLIENT} lizard-assetboard-tbl-cols-b-asset text-left ${FIELD_CLASSES.BOLD}`
      return this.isSelectedAsset(item) ? `${base} lizard-table-cell-reserved` : base
    },
    
    launchColumnClass(value, key, item) {
      const base = `${FIELD_CLASSES.COMMON_CLIENT} lizard-assetboard-tbl-cols-b-other text-center`
      return this.isRejectedAsset(item) ? `${base} lizard-table-cell-reserved` : base
    },

    // ===== UTILITY METHODS =====

    /**
     * Show toast notification
     * @param {String} message - Toast message
     * @param {String} variant - Toast variant (success, danger, info)
     */
    showToast(message, variant = 'info') {
      const titleMap = {
        success: 'Success',
        danger: 'Error',
        warning: 'Warning',
        info: 'Info'
      }

      this.$bvToast.toast(message, {
        title: titleMap[variant] || 'Notification',
        variant,
        solid: true
      })
    }
  }
}
</script>

<style scoped>
/* ===== NOTES LIST STYLING ===== */
.notes-list {
  list-style-type: none;
  padding: 0;
  margin: 0;
}

.notes-list li {
  font-size: 14px;
  font-weight: bold;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  justify-content: center;
  align-items: center;
  padding: 0;
  margin: 0;
}

.notes-list li span.date {
  color: #ccc;
}

.notes-list li span.author {
  color: #999;
}

.notes-list li span.note {
  color: #666;
}

/* ===== TABLE CAPTION STYLING ===== */
.table-caption-loading {
  text-transform: uppercase;
  font-size: 10px;
  font-weight: bold;
}

.table-caption-info {
  text-transform: uppercase;
  font-size: 12px;
  font-weight: bold;
  opacity: 0.7;
}

/* ===== NOTE BUTTON STYLING ===== */
.note-button {
  background-color: transparent;
  padding: 0;
  border: 0;
  font-size: 10pt;
}

/* ===== USER AVATARS STYLING ===== */
div.lizard-user-avatars-list {
  display: inline-block;
  justify-content: center;
  align-items: center;
  font-size: 10px;
  color: #333;
  cursor: default;
  margin: 0 1px;
}
</style>

<style>
/* ===== GLOBAL TABLE STYLING ===== */
.lizard-activity-list-no-bold {
  font-weight: 100 !important;
}

.lizard-assetboard-tbl-cols-bold {
  font-weight: bold !important;
}

/* Base Table Columns */
.lizard-assetboard-tbl-cols {
  font-size: 13px !important;
  white-space: nowrap !important;
  overflow: hidden;
  text-overflow: ellipsis;
  justify-content: center;
  align-items: center;
}

/* Client View Headers */
.lizard-assetboard-tbl-cols-b-head {
  font-size: 14px !important;
  font-weight: bold !important;
  white-space: nowrap !important;
  overflow: hidden;
  text-overflow: ellipsis;
  justify-content: center;
  align-items: center;
}

/* Client View Cells */
.lizard-assetboard-tbl-cols-b {
  font-size: 16px !important;
  font-weight: 100;
  white-space: nowrap !important;
  overflow: hidden;
  text-overflow: ellipsis;
  justify-content: center;
  align-items: center;
}

/* ===== COLUMN WIDTH CLASSES ===== */
/* Asset Column - 5% */
.lizard-assetboard-tbl-cols-asset {
  min-width: calc(100vw / 20) !important;
  max-width: calc(100vw / 20) !important;
}

/* Asset Column when HQ Country is hidden (US Assets tab) - 5% + released 4.54% */
.lizard-assetboard-tbl-cols-asset-wide {
  min-width: calc((100vw / 20) + (100vw / 22)) !important;
  max-width: calc((100vw / 20) + (100vw / 22)) !important;
}

/* Merger Market Links - 3.3% */
.lizard-assetboard-tbl-cols-mm {
  min-width: calc(100vw / 30) !important;
  max-width: calc(100vw / 30) !important;
}

/* Partners/EMs - 4% */
.lizard-assetboard-tbl-cols-persons {
  min-width: calc(100vw / 25) !important;
  max-width: calc(100vw / 25) !important;
}

/* Labels (Subsector, Process, Priority, Deck) - 4% */
.lizard-assetboard-tbl-cols-labels {
  min-width: calc(100vw / 25) !important;
  max-width: calc(100vw / 25) !important;
}

/* Comments/Description - 12% */
.lizard-assetboard-tbl-cols-comments {
  min-width: calc(100vw / 8) !important;
  max-width: calc(100vw / 8) !important;
}

/* Other Columns - 4.54% */
.lizard-assetboard-tbl-cols-other {
  min-width: calc(100vw / 22) !important;
  max-width: calc(100vw / 22) !important;
}

/* Owner Column - 5% */
.lizard-assetboard-tbl-cols-owner {
  min-width: calc(100vw / 20) !important;
  max-width: calc(100vw / 20) !important;
}

/* Note Column - 1.85% */
.lizard-assetboard-tbl-cols-note {
  min-width: calc(100vw / 54) !important;
  max-width: calc(100vw / 54) !important;
}

/* ===== CLIENT VIEW COLUMN WIDTHS ===== */
/* Asset - 16.66% - 2% */
.lizard-assetboard-tbl-cols-b-asset {
  min-width: calc((100vw / 6) - (100vw * 3 / 100)) !important;
  max-width: calc((100vw / 6) - (100vw * 3 / 100)) !important;
}

/* Asset Column when HQ Country is hidden (US Assets tab) - as above + released 6.25% */
.lizard-assetboard-tbl-cols-b-asset-wide {
  min-width: calc((100vw / 6) - (100vw * 3 / 100) + (100vw / 16)) !important;
  max-width: calc((100vw / 6) - (100vw * 3 / 100) + (100vw / 16)) !important;
}

/* Merger Market - 3.3% */
.lizard-assetboard-tbl-cols-b-mm {
  min-width: calc(100vw / 30) !important;
  max-width: calc(100vw / 30) !important;
}

/* Labels (Subsector) - 6.66% + 1% */
.lizard-assetboard-tbl-cols-b-labels {
  min-width: calc((100vw / 15) + (100vw / 50)) !important;
  max-width: calc((100vw / 15) + (100vw / 50)) !important;
}

/* Comments/Description - 15% + 5% */
.lizard-assetboard-tbl-cols-b-comments {
  min-width: calc((100vw / 6.66) + (100vw * 5 / 100)) !important;
  max-width: calc((100vw / 6.66) + (100vw * 5 / 100)) !important;
}

/* Other (IBank, EBITDA, HQ Country) - 6.25% */
.lizard-assetboard-tbl-cols-b-other {
  min-width: calc(100vw / 16) !important;
  max-width: calc(100vw / 16) !important;
}

/* Launch - 6.25% + 1% */
.lizard-assetboard-tbl-cols-b-launch {
  min-width: calc((100vw / 16) + (100vw * 1.5 / 100)) !important;
  max-width: calc((100vw / 16) + (100vw * 1.5 / 100)) !important;
}

/* Owner - 6.25% + 1% */
.lizard-assetboard-tbl-cols-b-owner {
  min-width: calc((100vw / 16) + (100vw * 1.5 / 100)) !important;
  max-width: calc((100vw / 16) + (100vw * 1.5 / 100)) !important;
}

/* ===== TAG BOX STYLING ===== */
/* Subsector / Process / Priority / Deck cells get an arbitrary background
   colour per asset (injected at runtime from meta.colors), so the text is
   white with a soft dark shadow — that stays legible on light and dark
   fills alike. Needs to out-specify the shared `.lizard-candesic-table
   tbody td` colour in src/styles/logs.css, hence the longer selector. */
.lizard-assetboard-tagbox {
  color: #fff;
}
.lizard-candesic-table tbody td.lizard-assetboard-tagbox {
  color: #fff !important;
  font-weight: 600;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.45);
}

/* Cells with no colour from the API. A light neutral fill with dark text
   reads better than the old mid-grey (#c4c4c4), which forced white text
   onto a background too pale to carry it. Must follow the rule above. */
.lizard-candesic-table tbody td.table-lizard-assetboard-na {
  background-color: #eef2f6 !important;
  color: #64748b !important;
  font-weight: 500;
  text-shadow: none !important;
}

/* ===== RESERVED CELL HIGHLIGHT ===== */
.lizard-table-cell-reserved {
  /* background-color: #e6f7ff !important; */
  background-color: rgb(246, 246, 246) !important;
}
</style>
